import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Copy, Info, LifeBuoy, Phone, PhoneCall, Settings } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import { Note, NumberedSteps, ScreenHeading, StepScreen, TextAction, VexuNumberCard } from './ActivationPrimitives';
import { MobileActivation } from '../../hooks/useMobileActivation';
import { ActivationMethod, RuleId, getRule } from '../../data/mobileForwarding';

// Names the Android Phone app typically uses for each forwarding option.
const ANDROID_OPTION: Record<RuleId, string> = {
  all: 'Always forward',
  busy: 'Forward when busy',
  unanswered: 'Forward when unanswered',
  unreachable: 'Forward when unreachable'
};

// Typical carrier confirmations after dialing an activation code.
const SUCCESS_MESSAGES = ['Call forwarding activated', 'Registration successful', 'Service enabled', 'Forwarding number registered'];
interface ConnectPhoneScreenProps {
  a: MobileActivation;
  onBack?: () => void;
  onContactSupport?: () => void;
}

/**
 * "Connect your phone" — pick a method (only those supported for this phone,
 * carrier and answering mode) and follow its instructions on the same screen.
 */
export function ConnectPhoneScreen({
  a,
  onBack,
  onContactSupport
}: ConnectPhoneScreenProps) {
  const {
    isLight
  } = useTheme();
  const options: {
    id: ActivationMethod;
    label: string;
    icon: React.ElementType;
  }[] = [...(a.settingsAvail.available ? [{
    id: 'settings' as const,
    label: 'Phone settings',
    icon: Settings
  }] : []), ...(a.codeAvail.available ? [{
    id: 'code' as const,
    label: 'Carrier code',
    icon: PhoneCall
  }] : [])];
  const noneAvailable = options.length === 0;
  const method: ActivationMethod | null = options.some(o => o.id === a.method) ? a.method : options[0]?.id ?? null;
  return <StepScreen stepKey="connect" onBack={onBack} footer={noneAvailable ? <>
            {a.iosConditional && <Button fullWidth onClick={() => a.setScreen('switchConfirm')} data-id="element-156" data-mp="32uo3b2k16kh7">
                Switch to answer every call
              </Button>}
            <Button fullWidth variant="secondary" onClick={() => onContactSupport?.()} data-id="element-157" data-mp="3cpprnw6hfiiq">
              Contact support
            </Button>
          </> : <>
            <Button fullWidth onClick={a.confirmActivation} data-id="element-158" data-mp="3lrz1skxu99vl">
              I've enabled call forwarding
            </Button>
            <TextAction onClick={() => method && a.fail(method)} data-id="element-159" data-mp="3qph2jo1pcisi">Having trouble?</TextAction>
          </>} data-id="element-155" data-mp="13obj527zjmnm">
      <ScreenHeading title="Connect your phone" subtitle={a.mode === 'all' ? 'Forward every call to your Vexu number.' : "Forward the calls you can't answer to your Vexu number."} data-id="element-160" data-mp="3vi0p3aidao9g" />

      {noneAvailable ? <Note icon={LifeBuoy} tone="amber" data-id="element-161" data-mp="dcfc1vwdm9la">
          {a.iosConditional ? <>
              We don't have a verified code for your carrier to set up “when I can't answer” on iPhone. You can ask
              us for help, or let Vexu answer every call — that one can be set up from iPhone Settings.
            </> : <>We can't set this up automatically for your carrier yet. Our team can help you finish setup.</>}
        </Note> : <>
          {options.length > 1 && <div role="radiogroup" aria-label="Setup method" className={`mb-5 grid grid-cols-2 gap-1 rounded-2xl p-1 ${isLight ? 'bg-slate-200/60' : 'bg-white/[0.06]'}`} data-id="element-162" data-mp="2hjgp3aoujc1s">
              {options.map(({
          id,
          label,
          icon: Icon
        }) => {
          const selected = method === id;
          return <button key={id} type="button" role="radio" aria-checked={selected} onClick={() => a.chooseMethod(id)} className={`flex items-center justify-center gap-2 whitespace-nowrap rounded-xl py-2.5 text-[13px] font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${selected ? isLight ? 'bg-white text-slate-900 shadow-sm' : 'bg-white/10 text-white' : isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-163" data-mp="308x1owdt2uyk">
                    <Icon className={`h-4 w-4 ${selected ? isLight ? 'text-vexu-700' : 'text-vexu' : ''}`} data-id="element-164" data-mp="33oq9g6yo8mxq" />
                    {label}
                  </button>;
        })}
            </div>}

          {/* The code already contains the Vexu number — only phone settings needs it. */}
          {method === 'settings' && <VexuNumberCard data-id="element-165" data-mp="3hn6g5xwjadfv" />}

          <AnimatePresence mode="wait" initial={false} data-id="element-166" data-mp="3hmy2w4wi1645">
            <motion.div key={`${method}-${a.os}-${a.androidBrand}`} initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.16,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-167" data-mp="3mg9c2cos9yz8">
              {method === 'code' ? <CodeInstructions a={a} data-id="element-168" data-mp="1js0y28jpw68m" /> : <SettingsInstructions a={a} data-id="element-169" data-mp="1fr7bbii0gxhb" />}
            </motion.div>
          </AnimatePresence>
        </>}
    </StepScreen>;
}
function B({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isLight
  } = useTheme();
  return <span className={`font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-170" data-mp="n3bk2tb7nxrx">{children}</span>;
}
function CodeInstructions({
  a
}: {
  a: MobileActivation;
}) {
  const {
    isLight
  } = useTheme();
  const multi = a.steps.length > 1;
  const muted = isLight ? 'text-slate-500' : 'text-zinc-400';
  return <>
      <p className={`mb-3 text-[13px] leading-relaxed ${muted}`} data-id="element-171" data-mp="2cg6l51y8u7bm">
        {multi ? 'Dial each code' : 'Dial this code'}, wait for your carrier's
        confirmation, then come back to Vexu.
      </p>
      <ul className={`mb-4 overflow-hidden rounded-2xl border ${isLight ? 'divide-y divide-slate-100 border-slate-200 bg-white shadow-sm' : 'divide-y divide-white/[0.06] border-white/10 bg-white/5'}`} data-id="element-172" data-mp="15476c9rhd783">
        {a.steps.map(step => <CodeRow key={step.code} code={step.code} title={step.rules.map(r => getRule(r).title).join(' & ')} data-id="element-173" data-mp="1j4rod3pkmz0j" />)}
      </ul>
      {a.carrier.sharedCodeNote && a.mode === 'conditional' && <Note icon={Info} data-id="element-174" data-mp="mco8ehcbcjcx">{a.carrier.sharedCodeNote}</Note>}
      <p className={`mb-2 text-xs font-medium ${muted}`} data-id="element-175" data-mp="qgvxwgqli3c7">After dialing, your phone should show a message like:</p>
      <ul className="flex flex-wrap gap-1.5" data-id="element-176" data-mp="vduoipb1vs5n">
        {SUCCESS_MESSAGES.map(m => <li key={m} className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${isLight ? 'border-slate-200 bg-white text-slate-700' : 'border-white/10 bg-white/5 text-zinc-300'}`} data-id="element-177" data-mp="107grtqvbqjos">
            <CheckCircle2 className={`h-3 w-3 flex-shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-178" data-mp="5twoweraph0" />
            {m}
          </li>)}
      </ul>
    </>;
}
function CodeRow({
  code,
  title
}: {
  code: string;
  title: string;
}) {
  const {
    isLight
  } = useTheme();
  const [copied, setCopied] = useState(false);
  return <li className="px-4 py-3.5" data-id="element-179" data-mp="3iane5075h6ew">
      <p className={`text-xs font-medium ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-180" data-mp="3idro6e7aqwdh">{title}</p>
      <div className="mt-1.5 flex items-center gap-2" data-id="element-181" data-mp="3deotnnoy08jr">
        <code className={`min-w-0 flex-1 truncate font-mono text-[15px] font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-182" data-mp="38kubwpswe3nb">
          {code}
        </code>
        <button type="button" aria-label={copied ? 'Code copied' : 'Copy activation code'} onClick={() => {
        navigator.clipboard?.writeText(code).catch(() => undefined);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      }} className={`grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg transition-colors ${copied ? 'bg-vexu/10 text-vexu' : isLight ? 'border border-slate-200 bg-white text-slate-500' : 'bg-white/10 text-zinc-400'}`} data-id="element-183" data-mp="2ynppqpz1y86i">
          {copied ? <CheckCircle2 className="h-4 w-4" data-id="element-184" data-mp="8d3lafeb2fx5" /> : <Copy className="h-4 w-4" data-id="element-185" data-mp="cf8c76ddcyg3" />}
        </button>
        <a href={`tel:${encodeURIComponent(code)}`} className="flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg bg-vexu px-3 text-xs font-bold text-white transition-colors hover:bg-vexu-600" data-id="element-186" data-mp="cdnitk2sjljt">
          <Phone className="h-3.5 w-3.5" data-id="element-187" data-mp="3gmvlgrgjthy" />
          Dial
        </a>
      </div>
    </li>;
}
function SettingsInstructions({
  a
}: {
  a: MobileActivation;
}) {
  if (a.os === 'ios') {
    return <>
        <NumberedSteps steps={[<>Open iPhone <B data-id="element-189" data-mp="3bvolz0aynzg4">Settings</B> → <B data-id="element-190" data-mp="3bv59w07zs5a0">Apps</B> → <B data-id="element-191" data-mp="3bwpuoy4i89uo">Phone</B>.</>, <>Tap <B data-id="element-192" data-mp="3gtye121e2jxi">Call Forwarding</B>. With two lines, pick the one you're setting up.</>, <>Turn on <B data-id="element-193" data-mp="2x750dgtpbo42">Call Forwarding</B>, tap <B data-id="element-194" data-mp="2x8g27b3l6lsj">Forward To</B>, and enter your <B data-id="element-195" data-mp="2x9r7xggle3ma">Vexu number</B>.</>, <>Come back to Vexu.</>]} data-id="element-188" data-mp="3prsd2kqjfrod" />
        <Note icon={Info} data-id="element-196" data-mp="2saobulilb7qg">
          This forwards <b data-id="element-197" data-mp="368uq8i7xqezq">every</b> call to Vexu. If you don't see Call Forwarding, tap “Having trouble?” and we'll show
          you another way.
        </Note>
      </>;
  }
  const samsung = a.androidBrand === 'samsung';
  const options = a.activeRules.map(r => ANDROID_OPTION[r]);
  const single = options.length === 1;
  return <>
      <NumberedSteps steps={[<>Open the <B data-id="element-199" data-mp="3pw2hhgm6fev8">Phone</B> app, tap the <B data-id="element-200" data-mp="3pwlow3e7akv0">three-dot menu</B>, then <B data-id="element-201" data-mp="3pvsrht14t7pj">Settings</B>.</>, samsung ? <>Open <B data-id="element-202" data-mp="3kwplo6dbuxgi">Supplementary services</B> → <B data-id="element-203" data-mp="3kyk8xrc2n1nr">Call forwarding</B> → <B data-id="element-204" data-mp="3kzc26vi74nav">Voice calls</B>.</> : <>Open <B data-id="element-205" data-mp="2sny98yvu66s">Calls</B> (or <B data-id="element-206" data-mp="2se3z96t1cva">Calling accounts</B>) → <B data-id="element-207" data-mp="2pjnttutqe7o">Call forwarding</B> → <B data-id="element-208" data-mp="3ty78s8p5jzep">Voice</B>.</>, single ? <>Tap <B data-id="element-209" data-mp="3tzqmbrjfbwcs">{options[0]}</B>, enter your <B data-id="element-210" data-mp="3p0eeoqfbm177">Vexu number</B>, and turn it on.</> : <>
              For each option below, enter your <B data-id="element-211" data-mp="3k9elnw9bo39z">Vexu number</B> and turn it on:
              <span className="mt-1.5 block space-y-1" data-id="element-212" data-mp="3k8muqyqo6tq1">
                {options.map(o => <span key={o} className="flex items-center gap-2" data-id="element-213" data-mp="3fb4lf78qqchi">
                    <span className="h-1 w-1 flex-shrink-0 rounded-full bg-vexu" aria-hidden data-id="element-214" data-mp="3actbyxmcy3zt" />
                    <B data-id="element-215" data-mp="bt37cgcok5iw">{o}</B>
                  </span>)}
              </span>
            </>, <>Come back to Vexu.</>]} data-id="element-198" data-mp="3b6mtg8e15nj8" />
      <Note icon={Info} data-id="element-216" data-mp="1vorahuo5ir4">If you have two SIMs, pick the one you're setting up. Menu names can differ slightly between phones.</Note>
    </>;
}